import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { supabase } from '../../lib/supabase';
import { uniqueChannelName } from '../../lib/realtime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFeather, faFolder } from '@fortawesome/free-solid-svg-icons';
import { Badge, Button, Card, Input, PulseDot, SegmentedControl, Table } from '../../components/ui';
import type { TableColumn } from '../../components/ui';
import AgentSyncModal from './AgentSyncModal';

const SKILLS_NAV = '__skills__';
const isSkillPath = (relPath: string) => relPath.startsWith('skills/');

const sourceLabel = (origin: string) => {
  if (origin === 'workspace') return 'Workspace';
  if (origin === 'plugin') return 'Plugin';
  if (origin === 'bundled') return 'Bundled';
  return origin || 'Unknown';
};

const parseSkillMeta = (content: string | null, relPath: string) => {
  const parts = relPath.split('/');
  const origin = parts[1] || 'unknown';
  const fallbackName = parts[2] || relPath;
  let name = fallbackName;
  let description = '';
  const fm = content?.match(/^---\r?\n([\s\S]*?)\r?\n---/);
  if (fm) {
    const nameMatch = fm[1].match(/^name:\s*["']?([^"'\n]+)/m);
    const descMatch =
      fm[1].match(/^description:\s*["']([^"']+)["']/m) ||
      fm[1].match(/^description:\s*["']?([^\n]+)/m);
    if (nameMatch?.[1]) name = nameMatch[1].trim();
    if (descMatch?.[1]) description = descMatch[1].trim().replace(/^['"]|['"]$/g, '');
  }
  return { name, description, source: sourceLabel(origin) };
};

type SkillRow = {
  id: string;
  name: string;
  source: string;
  description: string;
} & Record<string, unknown>;

interface AgentRuntime {
  id: string;
  slug: string;
  name: string;
  kind: 'openclaw' | 'hermes';
  workspace_root: string | null;
  config_path: string | null;
  is_active: boolean;
  last_seen_at: string | null;
}

interface AgentFile {
  id: string;
  runtime_id: string;
  rel_path: string;
  kind: 'brain' | 'gateway_config';
  restart_required: boolean;
  disk_content: string | null;
  disk_sha256: string | null;
  disk_updated_at: string | null;
  draft_content: string | null;
  draft_updated_at: string | null;
  pending_sync: boolean;
  last_applied_at: string | null;
}

const liveValue = (file: AgentFile | null | undefined) =>
  file ? (file.draft_content ?? file.disk_content ?? '') : '';

const isDirty = (file: AgentFile | null | undefined) => {
  if (!file) return false;
  if (file.draft_content == null) return false;
  return file.draft_content !== (file.disk_content ?? '');
};

const formatWhen = (value: string | null) => {
  if (!value) return 'never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'never';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default function AgentsPanel() {
  const { user } = useUser();
  const [runtimes, setRuntimes] = useState<AgentRuntime[]>([]);
  const [files, setFiles] = useState<AgentFile[]>([]);
  const [runtimeId, setRuntimeId] = useState<string | null>(null);
  const [fileId, setFileId] = useState<string | null>(null);
  const [editor, setEditor] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [skillSearch, setSkillSearch] = useState('');
  const seenDisk = useRef<Record<string, string | null>>({});
  const saveTimer = useRef<number | null>(null);
  const editorRef = useRef(editor);
  editorRef.current = editor;

  const selectedRuntime = runtimes.find((r) => r.id === runtimeId) || null;
  const runtimeFiles = useMemo(
    () => files.filter((f) => f.runtime_id === runtimeId),
    [files, runtimeId]
  );
  const brainFiles = useMemo(
    () => runtimeFiles.filter((f) => !isSkillPath(f.rel_path)),
    [runtimeFiles]
  );
  const skillFiles = useMemo(
    () => runtimeFiles.filter((f) => isSkillPath(f.rel_path)),
    [runtimeFiles]
  );
  const skillsOpen = fileId === SKILLS_NAV;
  const selectedFile = skillsOpen ? null : runtimeFiles.find((f) => f.id === fileId) || null;
  const editingSkill = !!(selectedFile && isSkillPath(selectedFile.rel_path));
  const skillsNavActive = skillsOpen || editingSkill;
  const dirtyFiles = runtimeFiles.filter(isDirty);
  const queuedFiles = runtimeFiles.filter((f) => f.pending_sync);
  const skillRows = useMemo((): SkillRow[] => {
    const q = skillSearch.trim().toLowerCase();
    return skillFiles
      .map((file) => {
        const meta = parseSkillMeta(file.disk_content, file.rel_path);
        return {
          id: file.id,
          name: meta.name,
          source: meta.source,
          description: meta.description,
        };
      })
      .filter((row) => {
        if (!q) return true;
        return `${row.name} ${row.source} ${row.description}`.toLowerCase().includes(q);
      });
  }, [skillFiles, skillSearch]);
  const skillColumns: TableColumn<SkillRow>[] = [
    { key: 'name', label: 'Name', mono: true },
    { key: 'source', label: 'Source' },
    {
      key: 'description',
      label: 'Description',
      render: (value) => (
        <span style={{ whiteSpace: 'normal' }}>
          {typeof value === 'string' && value.length > 120 ? `${value.slice(0, 117)}…` : (value as string) || '—'}
        </span>
      ),
    },
  ];

  const load = useCallback(async () => {
    try {
      setError(null);
      const [{ data: runtimeRows, error: runtimeError }, { data: fileRows, error: fileError }] =
        await Promise.all([
          supabase.from('agent_runtimes').select('*').order('name'),
          supabase.from('agent_files').select('*').order('rel_path'),
        ]);
      if (runtimeError) throw runtimeError;
      if (fileError) throw fileError;
      const nextRuntimes = (runtimeRows || []) as AgentRuntime[];
      const nextFiles = (fileRows || []) as AgentFile[];
      setRuntimes(nextRuntimes);
      setFiles(nextFiles);
      setRuntimeId((current) => current || nextRuntimes[0]?.id || null);
    } catch (err) {
      console.error('Failed to load agent brains', err);
      setError('Failed to load agent files. Has the migration been applied?');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!runtimeId) return;
    if (fileId === SKILLS_NAV) return;
    const stillThere = runtimeFiles.some((f) => f.id === fileId);
    if (!stillThere) setFileId(brainFiles[0]?.id || SKILLS_NAV);
  }, [runtimeFiles, brainFiles, runtimeId, fileId]);

  useEffect(() => {
    if (!selectedFile) {
      setEditor('');
      setConflict(false);
      return;
    }
    setEditor(liveValue(selectedFile));
    seenDisk.current[selectedFile.id] = selectedFile.disk_sha256;
    setConflict(false);
  }, [selectedFile?.id]);

  useEffect(() => {
    if (!selectedFile) return;
    const previous = seenDisk.current[selectedFile.id];
    if (previous && selectedFile.disk_sha256 && previous !== selectedFile.disk_sha256) {
      const draft = editorRef.current;
      const disk = selectedFile.disk_content ?? '';
      if (draft !== disk && isDirty({ ...selectedFile, draft_content: draft })) {
        setConflict(true);
      } else if (draft === (previous && liveValue(selectedFile))) {
        setEditor(selectedFile.disk_content ?? '');
        setConflict(false);
      } else if (!isDirty(selectedFile)) {
        setEditor(selectedFile.disk_content ?? '');
        setConflict(false);
      } else {
        setConflict(true);
      }
    }
    seenDisk.current[selectedFile.id] = selectedFile.disk_sha256;
  }, [selectedFile?.disk_sha256, selectedFile?.id]);

  useEffect(() => {
    const channel = supabase
      .channel(uniqueChannelName('agent_brains'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agent_runtimes' }, load)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agent_files' }, load)
      .subscribe();
    const poll = window.setInterval(load, 15000);
    return () => {
      supabase.removeChannel(channel);
      window.clearInterval(poll);
    };
  }, [load]);

  const persistDraft = useCallback(
    async (file: AgentFile, content: string) => {
      setSaving(true);
      try {
        const { error: updateError } = await supabase
          .from('agent_files')
          .update({
            draft_content: content,
            draft_updated_at: new Date().toISOString(),
            draft_updated_by: user?.id ?? null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', file.id);
        if (updateError) throw updateError;
      } catch (err) {
        console.error('Failed to save draft', err);
        setError('Draft did not save. Try again.');
      } finally {
        setSaving(false);
      }
    },
    [user?.id]
  );

  const handleEditorChange = (value: string) => {
    setEditor(value);
    if (!selectedFile) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      persistDraft(selectedFile, value);
    }, 700);
  };

  useEffect(() => {
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, []);

  const reloadFromDisk = async () => {
    if (!selectedFile) return;
    const disk = selectedFile.disk_content ?? '';
    setEditor(disk);
    setConflict(false);
    await persistDraft(selectedFile, disk);
  };

  const keepEditing = () => setConflict(false);

  const queueSync = async () => {
    if (dirtyFiles.length === 0) return;
    setSyncing(true);
    try {
      if (selectedFile && editor !== liveValue(selectedFile)) {
        await persistDraft(selectedFile, editor);
      }
      const ids = dirtyFiles.map((f) => f.id);
      const { error: updateError } = await supabase
        .from('agent_files')
        .update({ pending_sync: true, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (updateError) throw updateError;
      setSyncOpen(false);
      await load();
    } catch (err) {
      console.error('Failed to queue sync', err);
      setError('Could not queue the write.');
    } finally {
      setSyncing(false);
    }
  };

  const restartRequired = dirtyFiles.some((f) => f.restart_required || f.kind === 'gateway_config');

  if (isLoading) {
    return (
      <div className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>
        Loading agents…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            <FontAwesomeIcon icon={faFeather} style={{ marginRight: '8px' }} />
            On-disk brains
          </div>
          <p style={{ margin: 0, maxWidth: '640px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Disk is live. This screen is a delayed mirror. Edits stay drafts until Sync. Brain files reload next turn; gateway config restarts the agent.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {queuedFiles.length > 0 ? (
            <Badge tone="warn" pulse>
              Write queued
            </Badge>
          ) : null}
          <Button
            icon="send"
            disabled={dirtyFiles.length === 0 || queuedFiles.length > 0}
            onClick={() => setSyncOpen(true)}
          >
            Sync
          </Button>
        </div>
      </div>

      {error ? (
        <div style={{ color: 'var(--danger-400)', fontSize: 'var(--text-sm)' }}>{error}</div>
      ) : null}

      {runtimes.length > 1 ? (
        <SegmentedControl
          value={runtimeId || ''}
          onChange={setRuntimeId}
          options={runtimes.map((runtime) => ({
            id: runtime.id,
            label: runtime.name,
          }))}
        />
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)',
          gap: 'var(--space-4)',
          alignItems: 'start',
        }}
        className="max-md:!grid-cols-1"
      >
        <Card padding="12px">
          <div className="t-eyebrow" style={{ padding: '4px 8px 10px' }}>
            {selectedRuntime?.name || 'Agent'}
          </div>
          {selectedRuntime?.last_seen_at ? (
            <div style={{ padding: '0 8px 10px', fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
              Disk seen {formatWhen(selectedRuntime.last_seen_at)}
            </div>
          ) : (
            <div style={{ padding: '0 8px 10px', fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
              Waiting for the Mac worker to publish files
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {brainFiles.length === 0 && !selectedRuntime ? (
              <div style={{ padding: '12px 8px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                No files yet.
              </div>
            ) : (
              <>
                {brainFiles.map((file) => {
                  const active = file.id === fileId;
                  return (
                    <button
                      key={file.id}
                      type="button"
                      className="pressable hover-wash"
                      onClick={() => setFileId(file.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid ' + (active ? 'var(--border-strong)' : 'transparent'),
                        background: active ? 'var(--surface-active)' : 'transparent',
                        color: 'var(--text-body)',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                        {file.rel_path}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {file.pending_sync ? <PulseDot tone="warn" title="Write queued" /> : null}
                        {isDirty(file) ? <Badge tone="info">draft</Badge> : <Badge tone="idle">live</Badge>}
                      </span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="pressable hover-wash"
                  onClick={() => setFileId(SKILLS_NAV)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid ' + (skillsNavActive ? 'var(--border-strong)' : 'transparent'),
                    background: skillsNavActive ? 'var(--surface-active)' : 'transparent',
                    color: 'var(--text-body)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'var(--text-xs)' }}>
                    <FontAwesomeIcon icon={faFolder} />
                    Skills
                  </span>
                  <Badge tone={skillFiles.length ? 'idle' : 'neutral'}>{skillFiles.length}</Badge>
                </button>
              </>
            )}
          </div>
        </Card>

        <Card padding="0">
          {skillsOpen ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--border-hairline)',
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>Skills</div>
                  <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginTop: '4px' }}>
                    {skillFiles.length} on disk
                  </div>
                </div>
                <div style={{ width: '220px' }}>
                  <Input
                    icon="search"
                    fieldSize="sm"
                    placeholder="Search skills..."
                    value={skillSearch}
                    onChange={(e) => setSkillSearch(e.target.value)}
                  />
                </div>
              </div>
              {skillRows.length === 0 ? (
                <div style={{ padding: '28px 18px', color: 'var(--text-faint)', fontSize: 'var(--text-sm)' }}>
                  {skillFiles.length === 0
                    ? 'No skills synced yet. The Mac worker will fill this folder.'
                    : 'No skills match that search.'}
                </div>
              ) : (
                <Table
                  columns={skillColumns}
                  rows={skillRows}
                  onRowClick={(row) => setFileId(String(row.id))}
                  style={{ border: 0, borderRadius: 0, boxShadow: 'none' }}
                />
              )}
            </>
          ) : selectedFile ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--border-hairline)',
                }}
              >
                <div>
                  {editingSkill ? (
                    <button
                      type="button"
                      className="pressable"
                      onClick={() => setFileId(SKILLS_NAV)}
                      style={{
                        background: 'transparent',
                        border: 0,
                        padding: 0,
                        marginBottom: '6px',
                        color: 'var(--text-muted)',
                        fontSize: 'var(--text-2xs)',
                        cursor: 'pointer',
                      }}
                    >
                      ← Skills
                    </button>
                  ) : null}
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                    {selectedFile.rel_path}
                  </div>
                  <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginTop: '4px' }}>
                    {selectedFile.kind === 'gateway_config'
                      ? 'Gateway config · restart on sync'
                      : editingSkill
                        ? 'Skill file · no restart'
                        : 'Brain file · no restart'}
                    {' · '}
                    disk {formatWhen(selectedFile.disk_updated_at)}
                    {saving ? ' · saving draft' : ''}
                  </div>
                </div>
                {selectedFile.kind === 'gateway_config' ? (
                  <Badge tone="warn">restart</Badge>
                ) : editingSkill ? (
                  <Badge tone="neutral">skill</Badge>
                ) : (
                  <Badge tone="neutral">brain</Badge>
                )}
              </div>

              {conflict ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    flexWrap: 'wrap',
                    padding: '10px 16px',
                    background: 'var(--status-warn-soft)',
                    borderBottom: '1px solid var(--border-hairline)',
                  }}
                >
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--status-warn)' }}>
                    Disk changed while you were editing. Reload from disk or keep this draft.
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button size="sm" variant="secondary" onClick={reloadFromDisk}>
                      Reload disk
                    </Button>
                    <Button size="sm" variant="ghost" onClick={keepEditing}>
                      Keep editing
                    </Button>
                  </div>
                </div>
              ) : null}

              <textarea
                value={editor}
                onChange={(e) => handleEditorChange(e.target.value)}
                spellCheck={false}
                style={{
                  width: '100%',
                  minHeight: '520px',
                  border: 0,
                  resize: 'vertical',
                  padding: '16px',
                  background: 'transparent',
                  color: 'var(--text-body)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-sm)',
                  lineHeight: 1.55,
                  outline: 'none',
                }}
              />
            </>
          ) : (
            <div style={{ padding: '28px 18px', color: 'var(--text-faint)', fontSize: 'var(--text-sm)' }}>
              Select a file.
            </div>
          )}
        </Card>
      </div>

      <AgentSyncModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        onConfirm={queueSync}
        isSubmitting={syncing}
        restartRequired={restartRequired}
        fileNames={dirtyFiles.map((f) => f.rel_path)}
      />
    </div>
  );
}
